import { useCallback } from 'react'
import type { FormInstance } from 'antd'
import { Form } from 'antd'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { fetchUnits } from '@/api/units.api'

export interface MailFormValues {
    id?: string
    unitId: string
    unitName?: string
    serverUrl: string
    serverPort: number
    protocol: number
    mail: string
    name: string
    username: string
    password?: string
}

interface MailFormProps {
    form: FormInstance<MailFormValues>
    mode: 'create' | 'update'
    showUnitColumn: boolean
    onSubmit: (values: MailFormValues) => void
}

const PILL_INPUT = '!bg-white !text-[#1E2F41] disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

export const PROTOCOL_OPTIONS = [
    { value: 1, label: 'STARTTLS' },
    { value: 2, label: 'SSL/TLS' }
]

export function MailForm({ form, mode, showUnitColumn, onSubmit }: MailFormProps) {  
    const unitId = Form.useWatch('unitId', form)

    const loadUnits = useCallback(async ({ page, signal, searchText }: any) => {
        const response = await fetchUnits(page, 10, { keySearch: searchText?.trim() || undefined, status: 1 }, signal)
        const items = Array.isArray(response.items) ? response.items : []
        const hasNextPage = items.length >= 10
        return {
            options: items.map((unit: any) => ({ value: unit.id, label: unit.name })),
            hasNextPage,
            nextPage: hasNextPage ? page + 1 : undefined,
        }
    }, [])

    const handleFinish = (values: MailFormValues) => {
        const trimmedValues = Object.fromEntries(
            Object.entries(values).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
        ) as MailFormValues
        onSubmit(trimmedValues)
    }

    return (
        <Form
            form={form}
            layout="vertical"
            autoComplete="off"
            onFinish={handleFinish}
            requiredMark={(label, info) => <>{label}{info.required && <span className="text-destructive ml-1">*</span>}</>}
            className="space-y-6"
        >
            {showUnitColumn && (
                <Form.Item name="unitId" label="Đơn vị" rules={[{ required: true, message: 'Vui lòng chọn đơn vị' }]}>
                    <Select
                        cacheKey={`mail-units-select-${unitId}`}
                        initialPage={0}
                        lazy
                        size="large"
                        placeholder="Chọn đơn vị"
                        allowClear
                        className="w-full h-10 !text-[#1E2F41] [&_input]:!text-[#1E2F41]"
                        enableRemoteSearch
                        initialOptions={mode === 'update' && unitId ? [{ value: unitId, label: form.getFieldValue('unitName') }] : []}
                        loadOptions={loadUnits}
                        onLazyValueChange={(id) => form.setFieldsValue({ unitId: id })}
                    />
                </Form.Item>
            )}

            <div className="flex flex-col sm:flex-row gap-6 sm:gap-4">
                <Form.Item name="serverUrl" label="Máy chủ SMTP (Host/IP)" rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập máy chủ SMTP' }]} className="flex-1 !mb-0">
                    <Input placeholder=" Nhập host/IP" size="large" className={PILL_INPUT} />
                </Form.Item>

                <Form.Item name="serverPort" label="Cổng" rules={[{ required: true, message: 'Vui lòng nhập cổng' }]} className="w-full sm:w-[120px] !mb-0">
                    <Input type="number" placeholder="Ví dụ: 587" size="large" className={PILL_INPUT} />
                </Form.Item>
            </div>

            <Form.Item name="protocol" label="Giao thức" rules={[{ required: true, message: 'Vui lòng chọn giao thức' }]}>
                <Select
                    size="large"
                    placeholder="Chọn giao thức"
                    className="w-full h-10 !text-[#1E2F41]"
                    options={PROTOCOL_OPTIONS}
                    onValueChange={(val) => {
                        form.setFieldsValue({ protocol: val })
                        if (val === 1) form.setFieldsValue({ serverPort: 587 })
                        if (val === 2) form.setFieldsValue({ serverPort: 465 })
                    }}
                />
            </Form.Item>

            <Form.Item name="mail" label="Địa chỉ mail" rules={[{ required: true, type: 'email', message: 'Vui lòng nhập địa chỉ mail hợp lệ' }]}>
                <Input placeholder="Ví dụ: support@robot.com" size="large" className={PILL_INPUT} />
            </Form.Item>

            <Form.Item name="name" label="Tên hiển thị" rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập tên hiển thị' }]}>
                <Input placeholder="Ví dụ: Hệ thống quản trị robot" size="large" className={PILL_INPUT} />
            </Form.Item>

            <Form.Item name="username" label="Tên đăng nhập" rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập tên đăng nhập' }]}>
                <Input placeholder="Ví dụ: support@robot.com" size="large" autoComplete="off" className={PILL_INPUT} />
            </Form.Item>

            <Form.Item
                name="password"
                label="Mật khẩu"
                rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
            >
                <Input
                    type="password"
                    placeholder={mode === 'update' ? '••••••' : 'Nhập mật khẩu'}
                    size="large"
                    autoComplete="off"
                    className={`${PILL_INPUT} text-sm font-medium`}
                />
            </Form.Item>
        </Form>
    )
}
